import React, { Component } from 'react';
import AtcAutocomplete from '~/MedicinalBrandsAnalytics/AtcAutocomplete/AutocompleteContainer';
import FilterParameter from '~/utils/FilterParameter/FilterParameter';
import FilterParameterDescription from '~/utils/FilterParameter/FilterParameterDescription';
import Button, { Link } from '~/UI/Button';
import linkIconStyles from '~/UI/Button/icon.less';
import FormFieldError from '~/UI/FormFieldError/FormFieldError';
import Icon from '~/UI/Icon/Icon';
import ParameterInput from '~/UI/ParameterInput/ParameterInput';

import styles from './styles.less';
import Radio from '../Radio';
import { FilterOperator } from '~/Api/Product/Query';
import { TailwindTooltip } from '~/libs/ui/atoms/TailwindTooltip';

interface Props {
	availableParameters: FilterParameterDescription<string | string[]>[];
	autocompletes: {
		[key: string]: (reactProps: React.ComponentProps<typeof AtcAutocomplete>) => React.ReactElement;
	};
	filterParameters: FilterParameter<string | string[]>[];
	filterOperator?: FilterOperator;
	filterOperatorHide?: boolean;
	errors: { [key: string]: string[] };
	buttons: React.ReactNode;
	onSearch(parameters: FilterParameter<string | string[]>[], operator: FilterOperator): void;
}

interface State {
	availableParameters: FilterParameterDescription<string | string[]>[];
	filterParameters: FilterParameter<string | string[]>[];
	extraParameters: FilterParameter<string | string[]>[];
	operator: FilterOperator;
}

export default class FilterForm extends Component<Props, State> {
	static defaultProps = {
		autocompletes: {},
		errors: {},
		buttons: null,
	};

	state: State;

	constructor(props: Props) {
		super(props);
		const { filterParameters, availableParameters, filterOperator } = props;
		const availableNames = new Set(availableParameters.map((parameter) => parameter.name));
		const knownParameters = filterParameters.filter((parameter) => availableNames.has(parameter.name));

		const parameters: FilterParameter<string | string[]>[] =
			knownParameters.length > 0
				? knownParameters
				: [new FilterParameter<string | string[]>(availableParameters[0].name, availableParameters[0].initialValue)];
		const parameterNames = new Set(parameters.map((parameter) => parameter.name));

		this.state = {
			availableParameters: availableParameters.map((parameter) =>
				parameter.disabledUpdated(parameterNames.has(parameter.name))
			),
			filterParameters: parameters,
			extraParameters: filterParameters.filter((parameter) => !availableNames.has(parameter.name)),
			operator: filterOperator || FilterOperator.and,
		};
	}

	componentDidUpdate(prevProps: Props) {
		const { filterParameters, availableParameters } = this.props;

		if (prevProps.filterParameters !== filterParameters || prevProps.availableParameters !== availableParameters) {
			const availableNames = new Set(availableParameters.map((parameter) => parameter.name));
			this.setState({
				extraParameters: filterParameters.filter((parameter) => !availableNames.has(parameter.name)),
			});
		}
	}

	getCurrentFilterParameters(): FilterParameter<string | string[]>[] {
		const { filterParameters, extraParameters } = this.state;
		return [...filterParameters, ...extraParameters];
	}

	renderParameter(parameter: FilterParameter<string | string[]>, index: number) {
		const {
			props: { autocompletes, errors },
			state: { availableParameters, filterParameters },
		} = this;
		const showRemoveButton = filterParameters.length > 1;
		const showAddButton = index === filterParameters.length - 1 && availableParameters.length > filterParameters.length;
		const parameterErrors = errors[parameter.name];
		const hasParameterErrors = parameterErrors !== undefined && parameterErrors !== null && parameterErrors.length > 0;

		return (
			<div data-testid='filter-form-parameter' key={index}>
				<div className={styles.parameter}>
					{showRemoveButton ? (
						<div className={styles.removeButton}>
							<Link
								data-testid='remove-filter-parameter-button'
								styles={linkIconStyles}
								onClick={() => this.handleRemoveParameterClick(index)}
							>
								<Icon name='close'>remove</Icon>
							</Link>
						</div>
					) : null}

					<ParameterInput
						autocomplete={autocompletes[parameter.name]}
						parameter={parameter}
						parameters={availableParameters}
						onChange={(name, value) => this.handleFilterChange(index, name, value)}
					/>

					{showAddButton ? (
						<div className={styles.addButton}>
							<Link onClick={this.handleAddParameterClick}>Add search param?</Link>
						</div>
					) : null}
				</div>

				{hasParameterErrors ? (
					<div className={styles.error}>
						{parameterErrors.map((error) => (
							<FormFieldError key={error}>{error}</FormFieldError>
						))}
					</div>
				) : null}
			</div>
		);
	}

	handleFilterChange = (index: number, name: string, value: any) => {
		const { availableParameters, filterParameters } = this.state;
		const newFilterParameters = [...filterParameters];
		const parameter = newFilterParameters[index];

		if (name !== parameter.name) {
			const parameterDescription = this.props.availableParameters.find((description) => description.name === name);
			if (parameterDescription === undefined) {
				return;
			}

			newFilterParameters[index] = parameter.updated(
				name,
				typeof value === 'string' && typeof parameterDescription.initialValue === 'string'
					? value
					: parameterDescription.initialValue
			);

			const usedParameterNames = new Set(newFilterParameters.map((parameter) => parameter.name));
			this.setState({
				filterParameters: newFilterParameters,
				availableParameters: availableParameters.map((parameter) =>
					parameter.disabledUpdated(usedParameterNames.has(parameter.name))
				),
			});
		} else {
			newFilterParameters[index] = parameter.updated(name, value);
			this.setState({ filterParameters: newFilterParameters });
		}
	};

	handleAddParameterClick = () => {
		const { availableParameters, filterParameters } = this.state;
		const index = availableParameters.findIndex((parameter) => !parameter.disabled);
		const availableParameter = availableParameters[index];

		const newAvailableParameters = [...availableParameters];
		newAvailableParameters[index] = newAvailableParameters[index].disabledUpdated(true);

		this.setState({
			filterParameters: [
				...filterParameters,
				new FilterParameter<string | string[]>(availableParameter.name, availableParameter.initialValue),
			],
			availableParameters: newAvailableParameters,
		});
	};

	handleRemoveParameterClick = (index: number) => {
		const { availableParameters, filterParameters } = this.state;
		const filterParameter = filterParameters[index];
		const availableParameterIndex = availableParameters.findIndex(
			(parameter) => parameter.name === filterParameter.name
		);

		const newFilterParameters = [...filterParameters];
		newFilterParameters.splice(index, 1);

		const newAvailableParameters = [...availableParameters];
		newAvailableParameters[availableParameterIndex] =
			newAvailableParameters[availableParameterIndex].disabledUpdated(false);

		this.setState({
			filterParameters: newFilterParameters,
			availableParameters: newAvailableParameters,
		});
	};

	handleOperatorChange = (value: FilterOperator) => {
		this.setState({ operator: value });
	};

	handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		const {
			state: { operator },
		} = this;

		event.preventDefault();
		this.props.onSearch(this.getCurrentFilterParameters(), operator);
	};

	render() {
		const {
			props: { buttons, filterOperatorHide },
			state: { filterParameters, operator },
		} = this;

		return (
			<div>
				<form onSubmit={this.handleFormSubmit}>
					{filterParameters.map((parameter, index) => this.renderParameter(parameter, index))}

					{!filterOperatorHide && (
						<>
							<span className='relative mb-3 text-md'>Combine search parameters using</span>
							<TailwindTooltip
								reference={<Icon name='violet-question-mark' />}
								displayClass='inline-block ml-2 h-4 align-middle'
								position='bottom'
							>
								<p className='leading-snug'>
									<strong>Select AND/OR operator to specify your search criteria.</strong>
								</p>
								<p className='leading-snug'>
									<strong>AND:</strong> Returns results that match all selected search parameters at once. You will get
									less results, but more accurate.
								</p>
								<p className='leading-snug'>
									<strong>OR:</strong> Returns results that match at least one of selected search parameter. You will
									get more results, but less accurate.
								</p>
								<p className='leading-snug'>
									The default option is <strong>AND</strong>.
								</p>
							</TailwindTooltip>
							<div className='mb-6 flex items-center justify-center gap-x-6 gap-y-1'>
								<Radio
									checked={!operator || operator === FilterOperator.and}
									id='and'
									name='filter-operator'
									onChange={() => this.handleOperatorChange(FilterOperator.and)}
								>
									AND
								</Radio>
								<Radio
									checked={operator === FilterOperator.or}
									id='or'
									name='filter-operator'
									onChange={() => this.handleOperatorChange(FilterOperator.or)}
								>
									OR
								</Radio>
							</div>
						</>
					)}
					<div className={styles.buttons}>
						<Button styles={{ button: styles.searchButton }} type='submit' onClick={() => null}>
							Search
						</Button>
						{buttons}
					</div>
				</form>
			</div>
		);
	}
}
